import moment from "dayjs";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import useActionLock from "../../../hooks/useActionLock";
import Drawer from "../../../components/drawer/Drawer";
import { getNotificationTime } from "../../../helper/date-time-helper";
import { TEXT_DIRECTION } from "../../../helper/helperFunctions";
import {
  getAllNotifications,
  loadMoreNotifications,
} from "../../../redux/reducers/notifications/notifications.reducer";
import I18n, { i18n } from "../languageSelector";

const NotificationsDialog = ({
  showDlg,
  secondaryAction,
  allNotifications,
  markedAsReadHandle,
  getAllNotifications,
  loadMoreNotifications,
  markedReadSingleHandler,
  user,
  page,
  totalPages,
  loadingMore,
  unreadCount,
}) => {
  const [unReadNotification, setUnReadNotification] = useState([]);
  const [locked, runAction] = useActionLock();

  let isUserAdmin = user?.role === "admin" || user?.role === "owner";

  let adminNotifications = [
    "approved",
    "rejected",
    "incomplete",
    "formUpdate",
    "reSubmit",
    "submit",
    "roleUpdate",
  ];

  let inspectorNotifications = ["assigned", "approved", "rejected", "incomplete", "formUpdate", "reSubmit", "submit", "roleUpdate"];

  const allowedEvents = isUserAdmin ? adminNotifications : inspectorNotifications;

  const filteredNotifications = allNotifications.filter((item) => allowedEvents.includes(item.event));

  const hasMore = page < totalPages;

  useEffect(() => {
    if (showDlg) {
      getAllNotifications();
      setUnReadNotification([]);
    }
    // eslint-disable-next-line
  }, [showDlg]);

  useEffect(() => {
    let unreadArray = [];
    if (allNotifications && allNotifications.length) {
      unreadArray = allNotifications.filter((item) => !item.isRead).map((item) => item);
    }
    setUnReadNotification([...unreadArray]);
  }, [allNotifications]);

  const handleLoadMore = () => {
    if (hasMore && !loadingMore) {
      loadMoreNotifications(page + 1);
    }
  };

  // ---------- Resolve Report Title (field or message fallback) ----------
  const getReportTitle = (item) => {
    const title =
      item?.report?.name ||
      item?.reportName ||
      item?.task?.report?.name ||
      item?.report?.reportName;

    if (title) return title;

    if (item?.message) {
      // Example: The Report titled "XYZ" has been...
      const match = item.message.match(/titled\s+"(.+?)"/i);
      if (match && match[1]) return match[1];
    }

    return null;
  };

  return (
    <>
      <Drawer
        isOpen={showDlg}
        title={<I18n lng="global.NotificationsCenter" />}
        onClose={secondaryAction}
        primaryAction={markedAsReadHandle}
        buttonTitle1={<I18n lng="global.Close" />}
        buttonTitle2={<I18n lng="Notifications.MarkAllAsRead" />}
        customClasses={` p-0 ${TEXT_DIRECTION() === "rtl" ? "left-direction" : ""}`}
        secondaryButtonClasses={`text-sm font-semibold ${unreadCount > 0 ? "text-[#2D9CDB]" : "text-[rgba(45,156,219,0.5)]"}`}
        count={unreadCount > 0 ? unreadCount : ""}
        primaryActionButton={false}
        childClasses={"!px-2 !py-0"}
        primaryButtonClasses={"text-sm text-[#828282] font-semibold"}
      >
        <div className="p-0" style={{ scrollbarWidth: "none" }}>
          <ul style={{ scrollbarWidth: "none" }} className="notification list-unstyled mt-3">
            {filteredNotifications && filteredNotifications.length
              ? filteredNotifications.map((item, index) => {

                return (
                  <li
                    key={item._id || item.id || index}
                    onClick={() => { if (!item.isRead) runAction(async () => { await markedReadSingleHandler(item._id || item.id); }); }}
                    className="notification-item flex items-start justify-between py-3 border-b border-gray-200"
                    role="button"
                    style={locked ? { pointerEvents: "none", opacity: 0.7 } : {}}
                    aria-label={i18n("Notifications.OpenNotification")}
                  >
                    <div className={`flex items-start ${TEXT_DIRECTION() === "rtl" ? "mr-3" : "ml-3"}`}>

                      {/* Reserve dot space ALWAYS to keep alignment */}
                      <span
                        className="inline-block rounded-full"
                        style={{
                          width: 8,
                          height: 8,
                          marginTop: 6,
                          ...(TEXT_DIRECTION() === "rtl" ? { marginLeft: 12 } : { marginRight: 12 }),
                          backgroundColor: item.isRead ? "transparent" : "#2D9CDB"
                        }}
                        aria-hidden="true"
                      />

                      <div className={`flex flex-col ${TEXT_DIRECTION() === "rtl" ? "text-right" : "text-left"} break-words`}>
                        {/* Title (event heading) – bold if unread */}
                        <div
                          className={`text-sm text-[#333333CC] ${
                            item.isRead ? "font-semibold" : "font-extrabold"
                          }`}
                        >
                          {item.event === "assigned"
                            ? i18n("Notifications.FormAssignment")
                            : item.event === "approved" || (isUserAdmin && item.event === "approved")
                              ? i18n("Notifications.ReportApproved")
                              : item.event === "rejected" || (isUserAdmin && item.event === "rejected")
                                ? i18n("Notifications.ReportRejected")
                                : item.event === "incomplete" || (isUserAdmin && item.event === "incomplete")
                                  ? i18n("Notifications.ReportIncomplete")
                                  : item.event === "roleUpdate"
                                    ? i18n("Notifications.RoleUpdated")
                                    : item.event === "formUpdate"
                                      ? i18n("Notifications.FormUpdated")
                                      : item.event === "reSubmit"
                                        ? i18n("Notifications.ReportReSubmitted")
                                        : i18n("Notifications.ReportSubmitted")}
                        </div>

                        <div className="h-1" />

                        {/* Report Name — supports field + message fallback */}
                        {getReportTitle(item) && (
                          <>
                            <div className="text-[11px] opacity-70">
                              {i18n("Notifications.ReportName")}
                            </div>
                            <div className="text-sm leading-snug break-words">
                              {getReportTitle(item)}
                            </div>
                            <div className="h-1" />
                          </>
                        )}

                        {/* ===================== */}
                        {/* Form Name (second)   */}
                        {/* ===================== */}
                        {(item?.report?.formDetail?.title ||
                          item?.form?.formDetail?.title ||
                          item?.formTitle) && (
                          <>
                            <div className="text-[11px] opacity-70">
                              {i18n("Notifications.FormName")}
                            </div>
                            <div className="text-sm leading-snug break-words">
                              {
                                item?.report?.formDetail?.title ||
                                item?.form?.formDetail?.title ||
                                item?.formTitle ||
                                i18n("Notifications.Untitled")
                              }
                            </div>
                            <div className="h-1" />
                          </>
                        )}

                        {/* By */}
                        <div className="text-[11px] opacity-70">{i18n("Notifications.By")}</div>
                        <div className="text-sm leading-snug break-words">
                          {item?.sender?.fullName ||
                            item?.receiver?.fullName ||
                            item?.createdBy ||
                            i18n("Notifications.Unknown")}
                        </div>

                        <div className="h-1" />

                        {/* On */}
                        <div className="text-[11px] opacity-70">{i18n("Notifications.On")}</div>
                        <div className="text-sm leading-snug">
                          {moment(item?.createdAt).format("DD/MM/YYYY hh:mm A")}
                        </div>
                      </div>
                    </div>

                    {/* Right-side relative time */}
                    <div className="pl-3 pr-3 flex-shrink-0">
                      <span className="text-sm text-[#828282]">
                        {moment(item.createdAt).locale("it").fromNow() !== "7 days ago"
                          ? getNotificationTime(item.createdAt)
                          : moment(item.createdAt).format("DD/MM/YYYY")}
                      </span>
                    </div>
                  </li>
                );
              })
              : null}
          </ul>

          {/* Load More button */}
          {hasMore && (
            <div className="flex justify-center py-3">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="text-sm font-semibold text-[#2D9CDB] hover:text-[#1a7ab5] disabled:opacity-50"
              >
                {loadingMore ? i18n("global.loading") : i18n("Form.LoadMore")}
              </button>
            </div>
          )}
        </div>
      </Drawer>
    </>
  );
};
const mapStateToProps = ({ notifications }) => ({
  page: notifications.page,
  totalPages: notifications.totalPages,
  loadingMore: notifications.loadingMore,
  unreadCount: notifications.unreadCount,
});
const mapDispatchToProps = {
  getAllNotifications,
  loadMoreNotifications,
};
export default connect(mapStateToProps, mapDispatchToProps)(NotificationsDialog);
